import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { DashboardShell } from "../../components/DashboardShell";
import { Button } from "../../components/ui/button";
import { Progress } from "../../components/ui/progress";

export default function QuestionnairePage() {
  const { caseId, measureId } = useParams();
  const navigate = useNavigate();
  const [measure, setMeasure] = useState(null);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/measures/${measureId}`).then((r) => {
      setMeasure(r.data);
      setAnswers(new Array(r.data.items.length).fill(null));
    }).catch((e) => toast.error(errMsg(e)));
  }, [measureId]);

  const options = useMemo(() => {
    if (!measure) return [];
    return measure.per_item_options ? measure.per_item_options[idx] : measure.options;
  }, [measure, idx]);

  if (!measure) return <DashboardShell title="Loading"><div className="h-8 w-8 rounded-full border-2 border-[#1E3F20] border-t-transparent animate-spin" /></DashboardShell>;

  const total = measure.items.length;
  const progress = ((idx) / total) * 100;

  const choose = (value) => {
    const next = [...answers];
    next[idx] = value;
    setAnswers(next);
    if (idx < total - 1) setTimeout(() => setIdx(idx + 1), 220);
  };

  const submit = async () => {
    if (answers.some((a) => a === null)) { toast.error("Please answer every question."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/responses`, { measure: measureId, answers });
      toast.success(`${measure.name} submitted. Your clinician will review your responses as part of the full evaluation.`);
      navigate(`/app/case/${caseId}/questionnaires`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardShell title={measure.name} subtitle={measure.full_name}>
      <div className="max-w-3xl">
        <div className="flex items-center justify-between mb-3">
          <p className="label-caps text-[#7E9B82]" data-testid="question-counter">Question {idx + 1} of {total}</p>
          <p className="text-xs text-[#5C5C57]">{measure.instructions}</p>
        </div>
        <Progress value={progress} className="h-2 mb-10 [&>div]:bg-[#1E3F20]" data-testid="questionnaire-progress" />

        <AnimatePresence mode="wait">
          <motion.div key={idx} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.25 }}>
            <p className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight mb-8" data-testid="question-text">
              {measure.items[idx]}
            </p>
            <div className="space-y-3">
              {options.map((o) => (
                <button key={o.value} type="button" data-testid={`${measureId}-option-${o.value}`}
                  onClick={() => choose(o.value)}
                  className={`w-full text-left p-5 rounded-xl border text-base transition-colors ${answers[idx] === o.value ? "border-[#1E3F20] bg-[#1E3F20]/5 font-semibold" : "border-[#E5E5DF] bg-white hover:border-[#7E9B82] hover:-translate-y-0.5 transition-transform"}`}>
                  {o.label}
                </button>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="flex justify-between mt-10">
          <Button variant="ghost" data-testid="questionnaire-back-btn" disabled={idx === 0} onClick={() => setIdx(idx - 1)}
            className="rounded-full text-[#5C5C57]"><ArrowLeft className="h-4 w-4 mr-2" /> Previous</Button>
          {idx === total - 1 && (
            <Button data-testid="questionnaire-submit-btn" disabled={submitting || answers.some((a) => a === null)} onClick={submit}
              className="rounded-full bg-[#1E3F20] hover:bg-[#2C5A2E] text-white transition-colors">
              {submitting ? "Submitting…" : "Submit responses"}
            </Button>
          )}
        </div>
        <p className="text-[11px] text-[#5C5C57] mt-8">{measure.citation}</p>
      </div>
    </DashboardShell>
  );
}
